import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./NoticeBoard.css";

function SkeletonList({ rows = 5 }) {
  return (
    <ul className="notice-list">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="notice-item">
          <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 4 }} />
          <div className="skeleton" style={{ height: 11, width: "40%" }} />
        </li>
      ))}
    </ul>
  );
}

// Works for every endpoint (notices, tenders, success, ...).
// - Accepts title / name / message as the text field
// - Accepts link / url as the href; if there is no link it renders plain text
// - Only shows the date when one exists
// API sends dates as YYYY-MM-DD. Show them as DD-MM-YYYY.
// (Plain string handling, no Date object, so time zones can never shift the day.)
function formatDate(value) {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(value);
}

// The description column (tender_details) is CKEditor HTML. Convert it to
// plain text safely (DOMParser never runs scripts) so it can be shown as a
// short preview under the heading.
function stripHtml(html = "") {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

// showDetails = false → classic row:  • Title ............ date
// showDetails = true  → heading + description below:
//                         Title              (plain text, not a link)
//                         description (dd-mm-yyyy)   ← date at the end, in brackets
//                         Read more →        (opens the attachment)
function NoticeRow({ item, showDetails = false }) {
  const title = item.title || item.name || item.message || "";
  // "#" is what the API returns when a record has no attachment
  const href = item.link && item.link !== "#" ? item.link : item.url;
  const description = showDetails ? stripHtml(item.details) : "";

  const inner = (
    <>
      <span className="notice-dot" />
      <span className="notice-text">
        {title}
        {!!item.isNew && <span className="badge-new">NEW</span>}
      </span>
    </>
  );

  const heading = href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="notice-link">
      {inner}
    </a>
  ) : (
    <span className="notice-link">{inner}</span>
  );

  if (showDetails) {
    return (
      <li className="notice-item notice-item-detailed">
        {/* title is plain text here; the attachment opens from "Read more" below */}
        <span className="notice-link">{inner}</span>
        {(description || item.date) && (
          <p className="notice-desc">
            {description}
            {item.date && (
              <span className="notice-date">
                {description ? " " : ""} ( Date: {formatDate(item.date)} )
              </span>
            )}
          </p>
        )}
        {href && (
          <a href={href} target="_blank" rel="noopener noreferrer" className="notice-readmore">
            Read more →
          </a>
        )}
      </li>
    );
  }

  return (
    <li className="notice-item">
      {heading}
      {item.date && <span className="notice-date">{formatDate(item.date)}</span>}
    </li>
  );
}

// endpoint is required so "View All" knows where to navigate
function NoticeList({ items, limit = 6, highlight = false, endpoint, showDetails = false }) {
  // Detailed boards (Success Stories) show every record in a fixed-height box
  // with a normal vertical scrollbar, so no auto-scroll and no "View All".
  const hasOverflow = !showDetails && items?.length > limit;
  const duration = (items?.length || 0) * (highlight ? 1.2 : 4); // seconds

  return (
    <>
      {hasOverflow ? (
        <div className="notice-scroll-wrap">
          <ul className="notice-list notice-list-auto" style={{ animationDuration: `${duration}s` }}>
            {[...items, ...items].map((item, i) => (
              <NoticeRow key={`${item.id ?? "row"}-${i}`} item={item} showDetails={showDetails} />
            ))}
          </ul>
        </div>
      ) : (
        <ul className="notice-list">
          {items?.map((item, i) => (
            <NoticeRow key={item.id ?? i} item={item} showDetails={showDetails} />
          ))}
        </ul>
      )}
      {hasOverflow && (
        <Link to={`/notice-board/${endpoint}`} className="view-more-btn">
          View All ({items.length}) ▼
        </Link>
      )}
    </>
  );
}

// NOTE: "mci" is checked first because "MCI Information" contains the
// letters "form" (infor-m-ation → "form"), which would otherwise match
// the "forms" theme.
function getThemeFromTitle(title = "") {
  const t = title.toLowerCase();
  if (t.includes("mci")) return "mci";
  if (t.includes("success")) return "success";
  if (t.includes("notice")) return "notices";
  if (t.includes("advertisement")) return "advertisements";
  if (t.includes("form")) return "forms";
  if (t.includes("exam") || t.includes("result")) return "results";
  if (t.includes("admission")) return "admission";
  if (t.includes("tender")) return "tenders";
  if (t.includes("contact")) return "contact";
  return "";
}

// showDetails → show each record as: heading (click opens attachment) + description below
export function NoticeBoard({ endpoint, title, icon, highlight = false, theme = "", showDetails = false }) {
  const { data, loading } = useApiData(endpoint);
  const resolvedTheme = theme || getThemeFromTitle(title);
  const classes = [
    "card",
    "notice-board",
    highlight ? "notice-board-highlight" : "",
    resolvedTheme ? `theme-${resolvedTheme}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <h3 className="section-title1">
        {resolvedTheme === "success" && icon && (
          <span className="board-icon" aria-hidden="true">{icon}</span>
        )}
        <span className="heading-text">{title}</span>
      </h3>
      <div className="notice-body">
        {loading ? (
          <SkeletonList />
        ) : (
          <NoticeList items={data} highlight={highlight} endpoint={endpoint} showDetails={showDetails} />
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Marquee Ticker — JS (requestAnimationFrame) driven.

   Why not a CSS animation? Because `animation-duration` had to be
   recalculated in React state (on resize / font-load / data
   refetch), and re-declaring animation-duration on a *running*
   CSS animation forces the browser to restart it from 0% — which
   is exactly the "snaps back to the start after a while" bug.

   Instead we keep the scroll position in a ref and advance it
   every frame by a fixed px/sec speed, wrapping with modulo once
   it passes the width of one full cycle. Because both rendered
   cycles are pixel-identical, the wrap is invisible. React
   re-renders (new data, resize, etc.) never touch posRef, so they
   can never cause a visible reset.

   Duplicate-message ambiguity fix:
   Previously, few/short notices were tiled back-to-back (no gaps)
   to fill the bar width, so 1–2 messages could appear MULTIPLE
   TIMES on screen simultaneously — impossible to tell if that's
   one message looping or several different messages.

   Fix: only tile items back-to-back when they already fill the bar
   on their own (many/long messages — visually distinct, no
   ambiguity). Otherwise, append a blank gap that is at least as
   wide as the visible bar after the item set. Since the loop period
   (cycle width) is then >= bar width, the first message-block always
   fully scrolls out of view before the next copy of it can enter —
   so a single message can never be visible twice at once.
   ============================================================ */
export function MarqueeTicker() {
  const { data: notices } = useApiData("marquee");

  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const cycleRef = useRef(null); // whole cycle: items + optional gap (drives wrap math)
  const itemsRef = useRef(null); // items only, unaffected by gapPx — used to decide the gap

  const [gapPx, setGapPx] = useState(0);

  const posRef = useRef(0);
  const singleWidthRef = useRef(0);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  const speedRef = useRef(70); // px per second — tune to taste

  // Decide whether items alone fill the bar (tile them, no gap needed)
  // or whether we need a blank gap >= bar width to avoid duplicate
  // messages being visible at the same time.
  useEffect(() => {
    if (!notices?.length) return;

    function recalcGap() {
      const wrapWidth = wrapRef.current?.offsetWidth || 0;
      const itemsWidth = itemsRef.current?.scrollWidth || 0;
      if (!wrapWidth || !itemsWidth) return;
      setGapPx(itemsWidth >= wrapWidth ? 0 : wrapWidth);
    }

    recalcGap();
    const ro = new ResizeObserver(recalcGap);
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (itemsRef.current) ro.observe(itemsRef.current);
    window.addEventListener("resize", recalcGap);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalcGap);
    };
  }, [notices]);

  // After every render, re-measure the real pixel width of one full
  // cycle (items + gap). Read fresh each rAF tick, so it's always in
  // sync without ever needing to touch posRef.
  useEffect(() => {
    if (cycleRef.current) {
      singleWidthRef.current = cycleRef.current.scrollWidth;
    }
  });

  // Single persistent animation loop for the life of the component.
  useEffect(() => {
    function step(timestamp) {
      if (lastTimeRef.current == null) lastTimeRef.current = timestamp;
      const dt = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;

      if (!pausedRef.current && singleWidthRef.current > 0) {
        posRef.current += speedRef.current * dt;
        if (posRef.current >= singleWidthRef.current) {
          posRef.current -= singleWidthRef.current; // seamless wrap, cycles are identical
        }
        if (trackRef.current) {
          trackRef.current.style.transform = `translateX(-${posRef.current}px)`;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  if (!notices?.length) return null;

  const renderItems = (setKey) =>
    notices.map((n, i) => (
      <a key={`${setKey}-${i}`} href={n.link} className="ticker-item" target="_blank" rel="noopener noreferrer">
        {n.title}
        {!!n.isNew && <span className="badge-new">NEW</span>}
        <span className="ticker-sep">•</span>
      </a>
    ));

  const renderCycle = (setKey) => (
    <div className="ticker-cycle">
      <div className="ticker-items" ref={setKey === "a" ? itemsRef : null}>
        {renderItems(setKey)}
      </div>
      {gapPx > 0 && (
        <span
          className="ticker-gap"
          style={{ display: "inline-block", width: gapPx, flexShrink: 0 }}
        />
      )}
    </div>
  );

  return (
    <div className="ticker-full-bleed">
      <div className="ticker-bar">
        <div
          className="ticker-wrap"
          ref={wrapRef}
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
        >
          <div className="ticker-track" ref={trackRef}>
            <div className="ticker-set" ref={cycleRef}>{renderCycle("a")}</div>
            <div className="ticker-set">{renderCycle("b")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ContactBoard() {
  const { data, loading } = useApiData("contact");
  return (
    <div className="card notice-board contact-board theme-contact">
      <h3 className="section-title1">{data?.title || "Contact Us"}</h3>
      <div className="notice-body">
        {loading ? (
          <SkeletonList rows={4} />
        ) : (
          <div
            className="contact-mini-html"
            dangerouslySetInnerHTML={{ __html: data?.detailsHtml || "" }}
          />
        )}
      </div>
    </div>
  );
}
